import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  untracked,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import {
  DateSelectionService,
  SidebarService,
  ToastService,
} from '../../services';
import { DashboardIcon } from '../../icons/dashboard-icon/dashboard-icon';
import { CalendarIcon } from '../../icons/calendar-icon/calendar-icon';
import { UsersIcon } from '../../icons/users-icon/users-icon';
import { InventoryIcon } from '../../icons/inventory-icon/inventory-icon';
import { FinanceIcon } from '../../icons/finance-icon/finance-icon';
import { ReminderIcon } from '../../icons/reminder-icon/reminder-icon';
import { ServiceIcon } from '../../icons/service-icon/service-icon';
import { MailIcon } from '../../icons/mail-icon/mail-icon';
import { MessagesIcon } from '../../icons/messages-icon/messages-icon';
import { PromotionIcon } from '../../icons/promotion-icon/promotion-icon';
import { PlansIcon } from '../../icons/plans-icon/plans-icon';
import { AppointmentCalendar } from '../appointment-calendar/appointment-calendar';
import { AuthService } from '../../../services/auth';
import { MembershipService, trialToneFor } from '../../../services/membership/membership.service';
import { MENU } from '../../../config/menu.config';
import { HasAnyDirective } from '../../directives/has-any.directive';
import { UsersStaff } from '../../icons/users-staff/users-staff';
import { ProfessionalList } from '../professional-list/professional-list';
import { SettingIcon } from '../../icons/setting-icon/setting-icon';
import { Modal } from '../modal/modal';
import { AgendaService } from '../../../services/business';
import { EventAdd } from '../event-add/event-add';
import { EventStatusCode, STATUS_DEF_MAP } from '../../api/eventStatus.token';
import { CreateAgendaEventDto } from '../../interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, distinctUntilChanged, filter, startWith } from 'rxjs/operators';
import { AgendaIcon } from '../../icons/agenda-icon/agenda-icon';
import { SchedulePendingIcon } from '../../icons/schedule-pending-icon/schedule-pending-icon';
import { WalkInsIcon } from '../../icons/walk-ins-icon/walk-ins-icon';
import { Finance } from '../../../features/finance/finance';
import { ReportsIcon } from '../../icons/reports-icon/reports-icon';
import { BusinessTypeService } from '../../../services/business/businessType.service';
import { BusinessService } from '../../../services/business/business.service';
import { BusinessType } from '../../models/business/businessType';
import { GracePeriodService, GracePeriodStatus } from '../../services/grace-period.service';
import { MenuGroup, MenuItem } from '../../models';

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterModule,
    DashboardIcon,
    CalendarIcon,
    UsersIcon,
    FinanceIcon,
    ReminderIcon,
    ServiceIcon,
    PromotionIcon,
    PlansIcon,
    AppointmentCalendar,
    HasAnyDirective,
    UsersStaff,
    ProfessionalList,
    SettingIcon,
    Modal,
    AgendaIcon,
    SchedulePendingIcon,
    WalkInsIcon,
    InventoryIcon,
    ReportsIcon,
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
})
export class Sidebar implements OnInit {
  // Mobile sidebar state from parent layout
  @Input() mobileOpen = false;
  @Output() mobileClose = new EventEmitter<void>();

  selected = 'Dashboard1';
  page = 'analytics';
  sidebarService = inject(SidebarService);
  isCollapsed = false;
  router = inject(Router);
  private auth = inject(AuthService);
  private membershipService = inject(MembershipService);
  private businessTypeService = inject(BusinessTypeService);
  private businessService = inject(BusinessService);
  showAgendaPanel = signal(true);
  currentBusinessTypeName = signal<string | null>(null);
  private bus = inject(DateSelectionService);
  agendaApi = inject(AgendaService);
  EventAddForm = EventAdd;
  modalBodyInputs: Record<string, unknown> = {};
  openForm = false;
  private statusDefs = inject(STATUS_DEF_MAP);
  private toastService = inject(ToastService);
  private bp = inject(BreakpointObserver);
  today = new Date();
  private miniDesktop = signal(true);
  savingEvent = signal(false);
  currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
      distinctUntilChanged()
    ),
    { initialValue: this.router.url }
  );
  activeRoute = signal<string>(this.router.url);
  isAgendasRoute = computed(() => this.currentUrl() === '/agendas');

  // Membership status signals
  trialDaysRemaining = toSignal(this.membershipService.trialDaysRemaining$, {
    initialValue: null,
  });
  membershipStatus = toSignal(this.membershipService.membershipStatus$, {
    initialValue: null,
  });
  isExpired = toSignal(this.membershipService.isExpired$, {
    initialValue: false,
  });

  /** A payment can add or remove features: show the menu items the membership has now */
  private refilterOnMembershipChange = effect(() => {
    this.membershipStatus();
    untracked(() => this.filterMenu());
  });

  private gracePeriodService = inject(GracePeriodService);
  private gracePeriodStatus = toSignal<GracePeriodStatus | null>(
    this.gracePeriodService.gracePeriodStatus$,
    { initialValue: null }
  );
  /** When true, add top padding so "CitaLink Pro" logo is not covered by the grace period banner */
  showGraceBannerOffset = computed(
    () =>
      !!this.gracePeriodStatus()?.isInGracePeriod &&
      !this.gracePeriodService.isBannerDismissed()
  );

  constructor() {
    effect(() => {
      this.isCollapsed = this.sidebarService.collapseEnabled();
      const isDesktop = this.isLgUp();

      if (isDesktop) {
        // On desktop: close mobile sidebar if it was open
        if (this.mobileOpen) {
          this.mobileClose.emit();
        }
      } else {
        // On mobile: reset desktop mini state
        this.miniDesktop.set(false);
      }

      const url = this.currentUrl();
      this.showAgendaPanel.set(url.startsWith('/agendas'));
    });
  }

  // Close mobile sidebar when navigating (called from router subscription)
  private closeOnNavigation() {
    if (!this.isLgUp() && this.mobileOpen) {
      this.mobileClose.emit();
    }
  }
  visibleMenu = signal(MENU);

  private readonly groupTitles: Record<MenuGroup, string> = {
    agenda: 'Agenda',
    business: 'Tu negocio',
    sales: 'Ventas y resultados',
    system: '',
  };

  /** Visible items split into titled sections, keeping the MENU order */
  menuSections = computed(() => {
    const sections: { key: string; title: string | null; items: MenuItem[] }[] = [];
    for (const item of this.visibleMenu()) {
      if (item.group === 'system') continue;
      const key = item.group ?? 'main';
      let section = sections.find((s) => s.key === key);
      if (!section) {
        section = { key, title: item.group ? this.groupTitles[item.group] : null, items: [] };
        sections.push(section);
      }
      section.items.push(item);
    }
    return sections;
  });

  /** Settings-type items pinned to the bottom of the sidebar */
  footerMenu = computed(() => this.visibleMenu().filter((m) => m.group === 'system'));
  // Desktop: use miniDesktop state, Mobile: use mobileOpen input
  isCollapsedOwn = computed(() =>
    this.isLgUp() ? this.miniDesktop() : !this.mobileOpen
  );
  ngOnInit(): void {
    // Filter menu based on permissions and features
    this.auth.permissionsChanges.subscribe(() => {
      this.filterMenu();
    });

    // Initial menu filter
    this.filterMenu();

    // Close mobile sidebar when route changes
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.closeOnNavigation();
      });

    // Load business type for dynamic service icon
    this.loadBusinessType();

    // Load membership to check features
    this.loadMembershipForFeatures();
  }

  /**
   * Filter menu items based on permissions and membership features
   */
  private filterMenu(): void {
    const filtered = MENU.filter((m) => {
      // First check permissions
      if (!this.auth.hasAny(m.anyOf)) {
        return false;
      }

      // If menu item requires a feature, check if user has it
      if (m.requiredFeature) {
        return this.membershipService.hasFeature(m.requiredFeature);
      }

      return true;
    });

    this.visibleMenu.set(filtered);
  }

  /**
   * Load membership to check features (async)
   */
  private loadMembershipForFeatures(): void {
    const userInfo = this.auth.getUserInfo();
    if (!userInfo?.business_id) {
      return;
    }

    // Check if we need to load membership for feature checking
    const menuItemsWithFeatures = MENU.filter(m => m.requiredFeature);
    if (menuItemsWithFeatures.length === 0) {
      return;
    }

    // Load membership asynchronously and update menu when loaded
    // We check the first feature to trigger membership loading
    this.membershipService.hasFeatureAsync(menuItemsWithFeatures[0].requiredFeature!).subscribe(() => {
      // When membership is loaded, re-filter menu
      this.filterMenu();
    });
  }

  /**
   * Re-filter menu when membership might have changed
   * This can be called externally if needed
   */
  refreshMenu(): void {
    this.filterMenu();
  }

  /**
   * Carga el tipo de negocio actual para mostrar el icono de servicios correspondiente
   */
  private loadBusinessType(): void {
    // Obtener el business ID del usuario actual
    const userInfo = this.auth.getUserInfo();
    if (!userInfo || !userInfo.business_id) {
      return;
    }

    // Cargar business types al store si no están cargados
    const store = this.businessTypeService.getStore();
    const typesSignal = store.getModel();
    const types = typesSignal();

    if (types.length === 0) {
      // Si el store está vacío, cargar los datos primero
      this.businessTypeService.fetchAndSetEntitiesToStore();
      // Esperar un momento y luego intentar de nuevo
      setTimeout(() => {
        this.loadBusinessType();
      }, 500);
      return;
    }

    // Obtener el business actual para conocer su tipo
    this.businessService.getBusinessById(userInfo.business_id).subscribe({
      next: (business: any) => {
        if (business && business.businessTypeId) {
          const businessType = types.find(
            (t: BusinessType) => t.businessTypeId === business.businessTypeId
          );
          if (businessType) {
            this.currentBusinessTypeName.set(businessType.name);
          }
        }
      },
      error: () => {
        // Si falla, usar null (icono genérico)
        this.currentBusinessTypeName.set(null);
      },
    });
  }

  /** Trial card color, warming up as the trial runs out */
  trialTone = computed(() => trialToneFor(this.trialDaysRemaining(), this.isExpired()));

  isLgUp = toSignal(
    this.bp.observe('(min-width: 1024px)').pipe(
      map((r) => r.matches),
      distinctUntilChanged()
    ),
    {
      initialValue:
        typeof window !== 'undefined' &&
        matchMedia('(min-width: 1024px)').matches,
    }
  );

  toggleMiniDesktop() {
    if (this.isLgUp()) this.miniDesktop.update((v) => !v);
  }

  closeMobileSidebar() {
    if (!this.isLgUp()) {
      this.mobileClose.emit();
    }
  }

  onAgendaQuickAction(ev: MouseEvent) {
    ev.stopPropagation();
    this.showAgendaPanel.update((v) => !v);
  }

  onSelect(menu: string, event: MouseEvent, route?: string): void {
    if (this.isExpired() && route !== '/settings/billing') {
      event.preventDefault();
      this.router.navigate(['/settings/billing']);
      return;
    }
    this.selected = this.selected === menu ? '' : menu;
  }

  hideAgendaPanel() {
    this.showAgendaPanel.set(false);
  }
  showAgendaPanelFn() {
    this.showAgendaPanel.set(true);
  }

  iconFor(key: string) {
    switch (key) {
      case 'dashboard':
        return 'dashboard';
      case 'agendas':
        return 'agendas';
      case 'customers':
        return 'customers';
      case 'staff':
        return 'staff';
      case 'inventory':
        return 'inventory';
      case 'finance':
        return 'finance';
      case 'services':
        return 'services';
      case 'reminders':
        return 'reminders';
      case 'emarketing':
        return 'emarketing';
      case 'wa':
        return 'wa';
      case 'promos':
        return 'promos';
      case 'bundles':
        return 'bundles';
      case 'settings':
        return 'settings';
      default:
        return '';
    }
  }

  onDayClick(date: Date) {
    this.bus.set(date);
  }

  onClick() {
    this.modalBodyInputs = {
      selectedDate: new Date(),
    };
    this.openForm = true;
  }

  onModalConfirm(payload: any): void {
    if (!payload) {
      //this.openForm = false;
      return;
    }

    this.savingEvent.set(true);
    const code = 'completed' as EventStatusCode;
    const body: CreateAgendaEventDto = {
      ...payload,
      statusId: this.statusDefs[code]?.eventStatusId,
    };

    this.agendaApi.createAgendaEvent(body).subscribe({
      next: (res) => {
        if (res.eventId) {
          this.openForm = false;
          this.toastService.show(
            'Evento creado correctamente, puedes verlo en el calendario',
            'success',
            4000
          );
          this.bus.set(this.today);
          this.savingEvent.set(false);
        }
      },
      error: (err: HttpErrorResponse) => {
        const api = err.error as { code?: string; errors?: string[]; message?: string; details?: string };
        const msg =
          (api?.errors?.length ? api.errors.join('\n') : null) ||
          api?.details ||
          api?.message ||
          err.message ||
          'Ocurrió un error inesperado.';
        this.toastService.show(msg, 'warning', 6000);
        this.savingEvent.set(false);
      },
    });
  }
}
